/**
 * Drop undefined, which Firestore refuses to store, without touching anything
 * that only LOOKS like a plain object.
 *
 * The prototype check is the important half. Five copies of this function
 * existed — here, in Itinerary, in App, and in the itinerary importer — and the
 * private ones all had it while this one, the one every write path actually
 * goes through, did not.
 * Recursing into a Date, a Timestamp or a FieldValue sentinel copies out its
 * enumerable properties as a plain object and destroys it: a serverTimestamp()
 * would be saved literally as `{ _methodName: 'serverTimestamp' }`, which then
 * fails the `createdAt is timestamp` rule check.
 *
 * Undefined array ENTRIES are dropped rather than preserved as holes, because
 * an array carrying one is rejected outright and a shorter array at least
 * writes.
 */
export function stripUndef<T>(v: T): T {
  if (Array.isArray(v)) return v.map(stripUndef).filter(i => i !== undefined) as unknown as T;
  if (v !== null && typeof v === 'object'
    && [Object.prototype, null].includes(Object.getPrototypeOf(v))) {
    return Object.fromEntries(Object.entries(v).filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, stripUndef(value)])) as T;
  }
  return v;
}

export function equal(a: unknown, b: unknown): boolean {
  const stable = (value: unknown) => JSON.stringify(value, (_, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
  return stable(a) === stable(b);
}
export function conflict(): never {
  throw new Error('This item changed on another device. Reopen it to review the latest version before saving again.');
}
export function mergeValue(latest: unknown, before: unknown, after: unknown): unknown {
  if (equal(before, after) || equal(latest, after)) return latest;
  if (!equal(latest, before)) conflict();
  return after;
}

type Item = { id?: string; [key: string]: unknown };
export function mergeItems(latest: Item[], before: Item[], after: Item[], seed?: Item[]): Item[] {
  // Legacy ID-less transport arrays cannot be merged safely by identity.
  // ponytail: reject overlapping edits; migrate stable IDs if this becomes common.
  if ([...latest, ...before, ...after].some(item => !item.id)) {
    return mergeValue(latest, before, after) as Item[];
  }
  const unique = (items: Item[]) => new Set(items.map(item => item.id)).size === items.length;
  if (![latest, before, after].every(unique)) conflict();
  let result = [...latest];
  for (const id of new Set([...before, ...after].map(item => item.id))) {
    const old = before.find(item => item.id === id);
    const next = after.find(item => item.id === id);
    if (equal(old, next)) continue;
    const current = result.find(item => item.id === id);
    if (!old || !next) {
      const value = mergeValue(current, old, next) as Item | undefined;
      if (!value) result = result.filter(item => item.id !== id);
      else if (current) result = result.map(item => item.id === id ? value : item);
      else result.push(value);
    } else {
      const source = current ?? seed?.find(item => item.id === id);
      if (!source) conflict(); // Never resurrect an item removed from the selected day.
      const merged = { ...source };
      for (const key of new Set([...Object.keys(old), ...Object.keys(next)])) {
        const value = mergeValue(source[key], old[key], next[key]);
        if (value === undefined) delete merged[key];
        else merged[key] = value;
      }
      result = current ? result.map(item => item.id === id ? merged : item) : [...result, merged];
    }
  }
  const retained = before.filter(item => after.some(next => next.id === item.id)).map(item => item.id);
  const desired = after.filter(item => retained.includes(item.id)).map(item => item.id);
  if (!equal(retained, desired)) {
    const present = retained.filter(id => latest.some(item => item.id === id));
    mergeValue(latest.filter(item => present.includes(item.id)).map(item => item.id),
      present, desired.filter(id => present.includes(id)));
    return [...after.flatMap(item => result.filter(value => value.id === item.id)),
      ...result.filter(item => !after.some(value => value.id === item.id))];
  }
  return result;
}
